export interface DoorPlanPoint { x: number; z: number }
export type DoorObstacle = "door" | "body" | "room" | "handle" | "flap";

/** Convex panel footprints, in metres. Touching is allowed; penetration is not. */
export function doorPolygonsOverlap(a: DoorPlanPoint[], b: DoorPlanPoint[]) {
  for (const polygon of [a, b]) {
    for (let i = 0; i < polygon.length; i++) {
      const p = polygon[i]!, q = polygon[(i + 1) % polygon.length]!;
      const axis = { x: p.z - q.z, z: q.x - p.x };
      const project = (point: DoorPlanPoint) => point.x * axis.x + point.z * axis.z;
      const aa = a.map(project), bb = b.map(project);
      if (Math.max(...aa) <= Math.min(...bb) + 1e-10 ||
          Math.max(...bb) <= Math.min(...aa) + 1e-10) return false;
    }
  }
  return true;
}

/** Follow the entire swept path, even when the final pose is clear again. */
export function stopDoorBeforeObstacle(
  from: number,
  to: number,
  width: number,
  obstacleAt: (angle: number) => DoorObstacle | undefined
): { angle: number; obstacle?: DoorObstacle } {
  // At most 1 mm of tip travel per sample: a 20 mm facade cannot be skipped.
  const steps = Math.max(1, Math.ceil(Math.abs(to - from) * width / 0.001));
  let safe = from;
  for (let step = 1; step <= steps; step++) {
    const candidate = from + (to - from) * step / steps;
    const obstacle = obstacleAt(candidate);
    if (obstacle) {
      let blocked = candidate;
      for (let iteration = 0; iteration < 12; iteration++) {
        const middle = (safe + blocked) / 2;
        if (obstacleAt(middle)) blocked = middle;
        else safe = middle;
      }
      // Stay a fraction of a millimetre before contact, including easing and
      // transform rounding when two leaves advance in the same frame.
      const travel = Math.max(0, Math.abs(safe - from) - 0.0002 / width);
      return { angle: from + Math.sign(to - from) * travel, obstacle };
    }
    safe = candidate;
  }
  return { angle: to };
}

export function doorProgressAtAngle(angle: number, fullAngle: number) {
  const ratio = Math.max(0, Math.min(1, angle / fullAngle));
  let low = 0, high = 1;
  for (let i = 0; i < 24; i++) {
    const p = (low + high) / 2;
    if (p * p * (3 - 2 * p) < ratio) low = p;
    else high = p;
  }
  return (low + high) / 2;
}
